import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faCalculator } from "@fortawesome/free-solid-svg-icons";
import {
  utilsService,
  CurrencyObject,
  LocationInfo,
} from "../../services/utils.service";
import { rateService, RatePayload, RateOption } from "../../services/rate.service";
import { countryService, Country } from "../../services/country.service";
import AddressAutocomplete, { AddressComponents } from "../common/AddressAutocomplete";
import { normalizeZip, isValidZipFormat, getZipFormatErrorMessage } from "../../config/zip.utils";
import "../../styles/utility/CalculatorForm.css";
import btnStyles from "../../styles/components/Button.module.css";

import usePackageValidation, { validateWeightValue, validateDimensionValue } from "../../hooks/usePackageValidation";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = String(tomorrow.getDate()).padStart(2, '0');
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const year = tomorrow.getFullYear();
  return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const day = String(maxDate.getDate()).padStart(2, '0');
  const month = String(maxDate.getMonth() + 1).padStart(2, '0');
  const year = maxDate.getFullYear();
  return `${year}-${month}-${day}`;
};


type UnitSystem = "metric" | "imperial";
type Currency = string;

export interface CalculatorFormData {
  idCarrier: number;
  iso2Origin: string;
  iso2Destination: string;
  height: number;
  width: number;
  length: number;
  weight: number;
  unitSystem: string;
  zipcodeOrigin: string;
  zipcodeDestination: string;
  addressLineOrigin: string;
  addressLineDestination?: string;
  cityOrigin: string;
  stateOrigin: string;
  pickupDate: string;
  fob?: number;
  currency?: string;
  parcelOrdDoc?: string;
  serviceType?: string;
  hasBattery?: boolean;
  cityDestiny?: string;
  stateDestiny?: string;
  municipalityDestiny?: string;
}

interface CalculatorFormProps {
  onCalculate: (results: RateOption[], formData: CalculatorFormData) => void;
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({ onCalculate }) => {
  const [currencies, setCurrencies] = useState<CurrencyObject[]>([]);
  const [originLocationData, setOriginLocationData] = useState<LocationInfo | null>(null);
  const [destinationLocationData, setDestinationLocationData] = useState<LocationInfo | null>(null);
  const [zipcodeOriginApiError, setZipcodeOriginApiError] = useState<string | null>(null);
  const [zipcodeDestinationApiError, setZipcodeDestinationApiError] = useState<string | null>(null);
  const [receiverCountries, setReceiverCountries] = useState<Country[]>([]);

  // Initial values for useForm
  const initialValues = {
    unitSystem: "imperial" as UnitSystem,
    currency: "" as Currency,
    origin: "US",
    destination: "",
    weight: "" as number | "",
    width: "" as number | "",
    height: "" as number | "",
    length: "" as number | "",
    fob: "" as number | "",
    zipcodeOrigin: "",
    zipcodeDestination: "",
    addressLineOrigin: "",
    addressLineDestination: "",
    municipalityDestiny: "",
    cityOrigin: "",
    stateOrigin: "",
    stateDestiny: "",
    pickupDate: getTomorrowDate(),
    parcelOrdDoc: "PARCEL",
    serviceType: "A",
    hasBattery: false,
  };

  // Validation rules
  const validationRules: any = {
    weight: (value: any, allValues: any) =>
      validateWeightValue(value, allValues?.unitSystem),
    width: (value: any, allValues: any) =>
      validateDimensionValue(value, "Ancho", allValues?.unitSystem),
    height: (value: any, allValues: any) =>
      validateDimensionValue(value, "Alto", allValues?.unitSystem),
    length: (value: any, allValues: any) =>
      validateDimensionValue(value, "Largo", allValues?.unitSystem),
    fob: (value: any) =>
      value === "" || Number(value) < 0 ? "Requerido (>= 0)" : null,
    zipcodeOrigin: (value: any) => (!value ? "Requerido" : null),
    zipcodeDestination: (value: any) => (!value ? "Requerido" : null),
    addressLineOrigin: (value: any) => (!value ? "Requerido" : null),
    addressLineDestination: (value: any) => (!value ? "Requerido" : null),
  };

  const { values, errors, handleChange, handleSubmit, setValues, setErrors } = useForm(
    initialValues,
    validationRules
  );

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await utilsService.getCurrencies();
        setCurrencies(data);
        if (data.length > 0) {
          const usd = data.find((c) => c.isO3Currency === "USD");
          if (usd) {
            setValues((prev: any) => ({ ...prev, currency: "USD" }));
          }
        }
      } catch (error) {
        console.error("Error cargando monedas", error);
      }
    };
    loadCurrencies();
  }, [setValues]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const countryParam = searchParams.get("country");
    const weightParam = searchParams.get("weight");

    if (countryParam || weightParam) {
      const sanitizedCountry = countryParam?.toUpperCase() === "CU" ? "" : (countryParam || "");
      setValues((prev: any) => ({
        ...prev,
        destination: sanitizedCountry || prev.destination,
        weight: weightParam ? Number(weightParam) || "" : prev.weight,
      }));
    }
  }, [searchParams, setValues]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { receivers } = await countryService.getCountryLists();
        setReceiverCountries(receivers);
      } catch (error) {
        console.error("Error fetching country lists in CalculatorForm:", error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!values.destination) {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
      return;
    }
    const country = receiverCountries.find((c) => c.iso2Country === values.destination);
    if (!country) return;
    if (values.serviceType === "A" && !country.air) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Aéreo no disponible para este destino",
      }));
    } else if (values.serviceType === "M" && !country.maritime) {
      setErrors((prev: any) => ({
        ...prev,
        destination: "Servicio Marítimo no disponible para este destino",
      }));
    } else {
      setErrors((prev: any) => ({ ...prev, destination: undefined }));
    }
  }, [values.destination, values.serviceType, receiverCountries, setErrors]);

  const handleCalculate = async () => {
    const payload: RatePayload = {
      iso2Origin: values.origin,
      iso2Destination: values.destination,
      serviceType: values.serviceType,
      height: Number(values.height),
      width: Number(values.width),
      length: Number(values.length),
      weight: Number(values.weight),
      parcelOrdDoc: values.parcelOrdDoc,
      currency: values.currency,
      unitSystem: values.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      fob: Number(values.fob),
      zipcodeOrigin: values.zipcodeOrigin,
      zipcodeDestination: values.zipcodeDestination,
      addressLineOrigin: values.addressLineOrigin,
      addressLineDestination: values.addressLineDestination,
      pickupDate: values.pickupDate,
      cityOrigin: originLocationData?.city || values.cityOrigin || "",
      stateOrigin: originLocationData?.state || values.stateOrigin || "",
      stateDestiny: destinationLocationData?.state || values.stateDestiny || "",
      municipalityDestiny: values.municipalityDestiny,
      externalCarrierName: "UPS",
    };

    const formData: CalculatorFormData = {
      idCarrier: 0, // will be set by parent when user selects a carrier
      iso2Origin: values.origin,
      iso2Destination: values.destination,
      height: Number(values.height),
      width: Number(values.width),
      length: Number(values.length),
      weight: Number(values.weight),
      unitSystem: values.unitSystem === "metric" ? "METRIC" : "IMPERIAL",
      zipcodeOrigin: values.zipcodeOrigin,
      zipcodeDestination: values.zipcodeDestination,
      addressLineOrigin: values.addressLineOrigin,
      addressLineDestination: values.addressLineDestination,
      cityOrigin: originLocationData?.city || values.cityOrigin || "",
      stateOrigin: originLocationData?.state || values.stateOrigin || "",
      pickupDate: values.pickupDate,
      fob: Number(values.fob),
      currency: values.currency,
      parcelOrdDoc: values.parcelOrdDoc,
      serviceType: values.serviceType,
      hasBattery: values.hasBattery,
      cityDestiny: destinationLocationData?.city || values.municipalityDestiny || "",
      stateDestiny: destinationLocationData?.state || values.stateDestiny || "",
      municipalityDestiny: values.municipalityDestiny,
    };

    try {
      const results = await rateService.calculateRate(payload);
      onCalculate(results, formData);
    } catch (error) {
      console.error("Error calculating rate", error);
      onCalculate([], formData);
    }
  };

  const handleSwap = () => {
    setValues((prev: any) => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
    }));
  };

  // Check validity for button disable
  const isFormValid =
    values.weight &&
    values.width &&
    values.height &&
    values.length &&
    values.fob !== "" &&
    values.zipcodeOrigin &&
    values.zipcodeDestination &&
    values.addressLineOrigin &&
    values.addressLineDestination &&
    Object.values(errors).every((e) => !e) &&
    !zipcodeOriginApiError &&
    !zipcodeDestinationApiError;


  const handleZipcodeOriginBlur = async () => {
    if (values.zipcodeOrigin) {
      const country = values.origin || "US";
      const zip = normalizeZip(values.zipcodeOrigin, country);
      setValues((prev: any) => ({ ...prev, zipcodeOrigin: zip }));

      if (!isValidZipFormat(zip, country)) {
        const errorMsg = getZipFormatErrorMessage(country);
        setZipcodeOriginApiError(errorMsg);
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          zipcodeOrigin: errorMsg,
        }));
        return;
      }

      setZipcodeOriginApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeOrigin: null,
      }));

      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });

        if (location) {
          setOriginLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    } else {
      setZipcodeOriginApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeOrigin: validationRules.zipcodeOrigin(values.zipcodeOrigin),
      }));
    }
  };

  const handleZipcodeDestinationBlur = async () => {
    if (values.zipcodeDestination) {
      const country = values.destination || "US";
      const zip = normalizeZip(values.zipcodeDestination, country);
      setValues((prev: any) => ({ ...prev, zipcodeDestination: zip }));

      if (!isValidZipFormat(zip, country)) {
        const errorMsg = getZipFormatErrorMessage(country);
        setZipcodeDestinationApiError(errorMsg);
        setErrors((prevErrors: any) => ({
          ...prevErrors,
          zipcodeDestination: errorMsg,
        }));
        return;
      }

      setZipcodeDestinationApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeDestination: null,
      }));

      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });

        if (location) {
          setDestinationLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    } else {
      setZipcodeDestinationApiError(null);
      setErrors((prevErrors: any) => ({
        ...prevErrors,
        zipcodeDestination: validationRules.zipcodeDestination(values.zipcodeDestination),
      }));
    }
  };

  const handleAddressSelect = async (addressData: AddressComponents) => {
    const country = values.origin || "US";
    const zip = normalizeZip(addressData.zipCode || "", country);

    setValues((prev: any) => ({
      ...prev,
      addressLineOrigin: addressData.fullAddress,
      municipalityOrigin: addressData.municipality,
      zipcodeOrigin: zip || prev.zipcodeOrigin,
      cityOrigin: addressData.city || prev.cityOrigin,
      stateOrigin: addressData.state || prev.stateOrigin,
    }));

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      addressLineOrigin: undefined,
      zipcodeOrigin: undefined,
    }));
    setZipcodeOriginApiError(null);

    if (zip) {
      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });
        if (location) {
          setOriginLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    }
  };

  const handleDestinationAddressSelect = async (addressData: AddressComponents) => {
    const country = values.destination || "US";
    const zip = normalizeZip(addressData.zipCode || "", country);

    setValues((prev: any) => ({
      ...prev,
      addressLineDestination: addressData.fullAddress,
      municipalityDestiny: addressData.municipality,
      zipcodeDestination: zip || prev.zipcodeDestination,
      cityDestiny: addressData.city || prev.cityDestiny,
      stateDestiny: addressData.state || prev.stateDestiny,
    }));

    setErrors((prevErrors: any) => ({
      ...prevErrors,
      addressLineDestination: undefined,
      zipcodeDestination: undefined,
    }));
    setZipcodeDestinationApiError(null);

    if (zip) {
      try {
        const location = await utilsService.getLocationByZipcode({
          iso2code: country,
          zipcode: zip,
        });
        if (location) {
          setDestinationLocationData(location);
        }
      } catch (err) {
        console.warn("Non-blocking zip lookup notice:", err);
      }
    }
  };

  return (
    <div className="calculator-form-wrapper">
      <form onSubmit={handleSubmit(handleCalculate)}>
        {/* Step 1: Origin and Destination */}
        <div className="step-container">
          <div className="step-header">
            <span className="step-number">1</span>
            <span className="step-label">¿Desde dónde envías?</span>
            <div className="step-line"></div>
          </div>

          <div className="form-section">
            <div className="grid-row-origin-dest">
              <div className="form-group custom-select-wrapper">
                <label htmlFor="origin">Origen</label>
                <select
                  id="origin"
                  name="origin"
                  value={values.origin}
                  onChange={handleChange}
                  disabled
                >
                  <option value="US">Estados Unidos</option>
                </select>
              </div>

              <button
                type="button"
                className="swap-button"
                onClick={handleSwap}
                disabled={true}
                title="Intercambiar"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
                </svg>
              </button>

              <div className="form-group custom-select-wrapper" style={{ position: "relative" }}>
                <label htmlFor="destination">Destino</label>
                <select
                  id="destination"
                  name="destination"
                  value={values.destination}
                  className={errors.destination ? "is-invalid" : ""}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset destination address and zip when destination country changes
                    setValues((prev: any) => ({
                      ...prev,
                      zipcodeDestination: "",
                      addressLineDestination: "",
                      municipalityDestiny: "",
                    }));
                    setZipcodeDestinationApiError(null);
                  }}
                >
                  <option value="">Seleccionar País</option>
                  {receiverCountries.map((c) => (
                    <option key={c.id} value={c.iso2Country}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.destination && (
                  <div className="invalid-feedback" style={{ position: "absolute", top: "100%", left: 0, display: "block", marginTop: "2px", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                    {errors.destination}
                  </div>
                )}
              </div>
            </div>

            <div className="grid-row-zipcodes">
              <div className="form-group">
                <label htmlFor="zipcodeOrigin">Código Postal Origen</label>
                <input
                  type="text"
                  id="zipcodeOrigin"
                  name="zipcodeOrigin"
                  placeholder="Ej: 33101"
                  value={values.zipcodeOrigin}
                  onChange={handleChange}
                  onBlur={handleZipcodeOriginBlur}
                  className={errors.zipcodeOrigin || zipcodeOriginApiError ? "is-invalid" : ""}
                />
                {(errors.zipcodeOrigin || zipcodeOriginApiError) && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {errors.zipcodeOrigin || zipcodeOriginApiError}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="zipcodeDestination">Código Postal Destino</label>
                <input
                  type="text"
                  id="zipcodeDestination"
                  name="zipcodeDestination"
                  placeholder={!values.destination ? "Selecciona un país primero" : "Ej: 1010"}
                  value={values.zipcodeDestination}
                  onChange={handleChange}
                  onBlur={handleZipcodeDestinationBlur}
                  disabled={!values.destination}
                  className={errors.zipcodeDestination || zipcodeDestinationApiError ? "is-invalid" : ""}
                  style={!values.destination ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                />
                {(errors.zipcodeDestination || zipcodeDestinationApiError) && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {errors.zipcodeDestination || zipcodeDestinationApiError}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group mt-3">
              <label htmlFor="addressLineOrigin">Dirección de Origen</label>
              <AddressAutocomplete
                value={values.addressLineOrigin}
                onChange={(val) => handleChange({ target: { name: 'addressLineOrigin', value: val } } as any)}
                onAddressSelect={handleAddressSelect}
                placeholder="Calle, Ave, etc. (Origen)"
                inputName="addressLineOrigin"
                apiKey={GOOGLE_API_KEY}
                inputClassName={errors.addressLineOrigin ? "is-invalid" : ""}
                country={values.origin.toLowerCase()}
              />
              {errors.addressLineOrigin && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {errors.addressLineOrigin}
                </div>
              )}
            </div>

            <div className="form-group mt-3">
              <label htmlFor="addressLineDestination">Dirección de Destino</label>
              <AddressAutocomplete
                value={values.addressLineDestination}
                onChange={(val) => handleChange({ target: { name: 'addressLineDestination', value: val } } as any)}
                onAddressSelect={handleDestinationAddressSelect}
                placeholder={!values.destination ? "Selecciona un país primero" : "Calle, Ave, etc. (Destino)"}
                inputName="addressLineDestination"
                apiKey={GOOGLE_API_KEY}
                inputClassName={errors.addressLineDestination ? "is-invalid" : ""}
                country={values.destination.toLowerCase()}
                disabled={!values.destination}
              />
              {errors.addressLineDestination && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {errors.addressLineDestination}
                </div>
              )}
            </div>

            <div className="form-group mt-3">
              <label htmlFor="pickupDate">Fecha de Recolección (Deseada)</label>
              <input
                type="date"
                id="pickupDate"
                name="pickupDate"
                value={values.pickupDate}
                onChange={handleChange}
                min={getTomorrowDate()}
                max={getMaxDate()}
              />
            </div>
          </div>
        </div>

        {/* Step 2: Package Information */}
        <div className="step-container">
          <div className="step-header" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
              <span className="step-number">2</span>
              <span className="step-label">Detalles de tu paquete</span>
              <div className="step-line" style={{ marginRight: '15px' }}></div>
            </div>
            
            <div style={{
              display: 'flex',
              backgroundColor: 'var(--brand-black, #0a0a0a)',
              borderRadius: '8px',
              padding: '4px',
              height: '36px',
              alignItems: 'center',
              boxSizing: 'border-box',
              gap: '4px',
              width: '180px',
              flexShrink: 0
            }}>
              <button
                type="button"
                onClick={() => {
                  setValues((prev: any) => ({
                    ...prev,
                    unitSystem: 'imperial',
                    width: "",
                    height: "",
                    length: "",
                    weight: ""
                  }));
                  setErrors((prev: any) => ({
                    ...prev,
                    width: undefined,
                    height: undefined,
                    length: undefined,
                    weight: undefined
                  }));
                }}
                style={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: values.unitSystem === 'imperial' ? '#222222' : 'transparent',
                  color: values.unitSystem === 'imperial' ? 'var(--brand-yellow, #ffd700)' : '#777777',
                  outline: 'none',
                  boxShadow: values.unitSystem === 'imperial' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none'
                }}
              >
                Imperial
              </button>
              <button
                type="button"
                onClick={() => {
                  setValues((prev: any) => ({
                    ...prev,
                    unitSystem: 'metric',
                    width: "",
                    height: "",
                    length: "",
                    weight: ""
                  }));
                  setErrors((prev: any) => ({
                    ...prev,
                    width: undefined,
                    height: undefined,
                    length: undefined,
                    weight: undefined
                  }));
                }}
                style={{
                  flex: 1,
                  height: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: values.unitSystem === 'metric' ? '#222222' : 'transparent',
                  color: values.unitSystem === 'metric' ? 'var(--brand-yellow, #ffd700)' : '#777777',
                  outline: 'none',
                  boxShadow: values.unitSystem === 'metric' ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none'
                }}
              >
                Métrico
              </button>
            </div>
          </div>

          <div className="form-section">
            {/* Tipo de Contenido: Paquete / Documento */}
            <div className="form-group" style={{ marginBottom: '18px' }}>
              <label style={{ fontWeight: 600, marginBottom: '6px', display: 'block', color: 'var(--brand-black)' }}>Tipo de Paquete</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div
                  style={{
                    flex: '1 1 0%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.25s ease',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => {
                    setValues((prev: any) => ({
                      ...prev,
                      parcelOrdDoc: 'PARCEL',
                      width: '',
                      height: '',
                      length: '',
                      weight: '',
                      fob: '',
                      hasBattery: false,
                    }));
                    setErrors({});
                  }}
                >
                  <input type="checkbox" checked={values.parcelOrdDoc === 'PARCEL'} readOnly style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>Caja</span>
                </div>
                <div
                  style={{
                    flex: '1 1 0%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'all 0.25s ease',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={() => {
                    setValues((prev: any) => ({
                      ...prev,
                      parcelOrdDoc: 'DOC',
                      width: '',
                      height: '',
                      length: '',
                      weight: '',
                      fob: '',
                      hasBattery: false,
                    }));
                    setErrors({});
                  }}
                >
                  <input type="checkbox" checked={values.parcelOrdDoc === 'DOC'} readOnly style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }} />
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>Documento</span>
                </div>
              </div>
            </div>

            {/* Dimensions Row (3 columns) */}
            <div className="grid-row-dimensions">
              <div className="form-group">
                <label htmlFor="width">Ancho ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                <input
                  type="number"
                  id="width"
                  name="width"
                  placeholder="Ej: 10"
                  value={values.width}
                  onChange={handleChange}
                  className={errors.width ? "is-invalid" : ""}
                />
                {errors.width && <div className="invalid-feedback">{errors.width}</div>}
              </div>
              <div className="form-group">
                <label htmlFor="height">Alto ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  placeholder="Ej: 10"
                  value={values.height}
                  onChange={handleChange}
                  className={errors.height ? "is-invalid" : ""}
                />
                {errors.height && <div className="invalid-feedback">{errors.height}</div>}
              </div>
              <div className="form-group">
                <label htmlFor="length">Largo ({values.unitSystem === "metric" ? "cm" : "in"})</label>
                <input
                  type="number"
                  id="length"
                  name="length"
                  placeholder="Ej: 10"
                  value={values.length}
                  onChange={handleChange}
                  className={errors.length ? "is-invalid" : ""}
                />
                {errors.length && <div className="invalid-feedback">{errors.length}</div>}
              </div>
            </div>

            {/* Details Row (4 columns changed to 3 columns: Peso, FOB, Moneda) */}
            <div className="grid-row-details">
              <div className="form-group">
                <label htmlFor="weight">Peso ({values.unitSystem === "metric" ? "kg" : "lbs"})</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  placeholder="Ej: 1"
                  value={values.weight}
                  onChange={handleChange}
                  className={errors.weight ? "is-invalid" : ""}
                />
                {errors.weight && <div className="invalid-feedback">{errors.weight}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="fob">FOB ({values.currency})</label>
                <input
                  type="number"
                  id="fob"
                  name="fob"
                  placeholder="Ej: 100"
                  value={values.fob}
                  onChange={handleChange}
                  className={errors.fob ? "is-invalid" : ""}
                />
                {errors.fob && <div className="invalid-feedback">{errors.fob}</div>}
              </div>
              <div className="form-group">
                <label htmlFor="currency">Moneda</label>
                <select
                  id="currency"
                  name="currency"
                  value={values.currency}
                  onChange={handleChange}
                  disabled={currencies.length === 0}
                >
                  {currencies.length > 0 ? (
                    currencies.map((curr) => (
                      <option key={curr.id} value={curr.isO3Currency}>
                        {curr.isO3Currency}
                      </option>
                    ))
                  ) : (
                    <option value="">Cargando...</option>
                  )}
                </select>
              </div>
            </div>

            {/* Batería — solo visual, no va al payload */}
            <div
              style={{
                marginTop: '12px',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: values.parcelOrdDoc === 'DOC' ? 'not-allowed' : 'pointer',
                opacity: values.parcelOrdDoc === 'DOC' ? 0.6 : 1,
                userSelect: 'none',
                transition: 'all 0.25s ease',
                outline: 'none',
                WebkitTapHighlightColor: 'transparent'
              }}
              onClick={() => {
                if (values.parcelOrdDoc !== 'DOC') {
                  setValues((prev: any) => ({ ...prev, hasBattery: !prev.hasBattery }));
                }
              }}
            >
              <input
                type="checkbox"
                checked={values.hasBattery}
                disabled={values.parcelOrdDoc === 'DOC'}
                readOnly
                style={{ width: '18px', height: '18px', cursor: 'pointer', outline: 'none' }}
              />
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--brand-black)' }}>
                El paquete contiene baterías (ej. Laptops, teléfonos, etc.)
              </span>
            </div>
          </div>
        </div>

        {/* Step 3: Calculate Button */}
        <div className="step-container no-border">
          <div className="step-header">
            <span className="step-number">3</span>
            <span className="step-label">Calcular tarifa</span>
            <div className="step-line"></div>
          </div>
          <button
            type="submit"
            className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
            disabled={!isFormValid}
            style={{ opacity: !isFormValid ? 0.7 : 1, cursor: !isFormValid ? 'not-allowed' : 'pointer' }}
          >
            <FontAwesomeIcon icon={faCalculator} className="mr-2" /> CALCULAR
          </button>
        </div>
      </form>
    </div>
  );
};

export default CalculatorForm;
